import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const copyByLang = {
  es: {
    metaTitle: "Casos Seleccionados | Puerto Rico Esports League",
    metaDescription: "Casos seleccionados de Puerto Rico Esports League en activaciones de marca, contenido original, eventos en vivo y experiencias enfocadas en esports.",
    ogDescription: "Ejemplos concretos del trabajo de PREL en medios, eventos y cruces con entretenimiento.",
    schemaName: "Casos Seleccionados | Puerto Rico Esports League",
    schemaDescription: "Casos y ejemplos del trabajo de Puerto Rico Esports League.",
    eyebrow: "Casos Seleccionados",
    title: "Casos que muestran cómo opera PREL.",
    intro:
      "PREL trabaja entre experiencias en vivo, momentos de marca, contenido original y narrativa centrada en gaming. Esta página reúne algunos ejemplos que ayudan a explicar cómo funciona la plataforma en la práctica y por qué puede ser relevante para marcas, colaboradores, medios y aliados estratégicos.",
    why: "Por qué importó",
    items: [
      {
        title: "PREL x WWE 2K",
        category: "Cruce con entretenimiento",
        summary:
          "Un momento que demostró la capacidad de PREL para conectar cultura gaming con propiedad intelectual reconocible, narrativa alrededor de talento y relevancia de marca frente a la audiencia.",
        outcome:
          "Ayudó a posicionar a PREL como algo más que una plataforma local de torneos, mostrando que también puede crear momentos con valor cultural, visibilidad mediática y potencial de expansión.",
        assetLabel: "Ver muestra de WWE",
        assetHref: "/media/PREL_WWE_Edit.mp4",
      },
      {
        title: "US Army Battleground",
        category: "Activación en vivo",
        summary:
          "Un evento competitivo de alta visibilidad que combinó producción en vivo, presencia de marca, participación de audiencia y contenido útil para recap y distribución posterior.",
        outcome:
          "Demostró la capacidad de PREL para ejecutar experiencias presenciales que funcionan tanto en venue como en contenido posterior para visibilidad ampliada.",
        assetLabel: "Ver recap",
        assetHref: "https://www.youtube.com/watch?v=SfsotLUQde0",
      },
      {
        title: "PREL: The Show",
        category: "Televisión y contenido original",
        summary:
          "El programa insignia de PREL consolidó un formato repetible entre televisión y digital, creando una propiedad mediática para gaming, talento, invitados y narrativa original.",
        outcome:
          "Fortaleció la base de desarrollo de audiencia, integración de aliados y posicionamiento mediático de largo plazo más allá de los eventos en vivo.",
        assetLabel: "Explorar El Show",
        assetHref: "/the-show",
      },
      {
        title: "School Wars",
        category: "Comunidad y juventud",
        summary:
          "Un formato competitivo enfocado en escuelas, diseñado alrededor de participación, visibilidad y talento emergente, ayudando a PREL a operar en la intersección de gaming, juventud y programación estructurada.",
        outcome:
          "Amplió la relevancia de PREL más allá de la competencia core al mostrar cómo la plataforma también puede servir para activación comunitaria y crecimiento futuro de audiencia.",
        assetLabel: "Ver muestra",
        assetHref: "https://www.youtube.com/watch?v=JO5b4eYqhEs",
      },
    ],
  },
  en: {
    metaTitle: "Selected Work | Puerto Rico Esports League",
    metaDescription: "Selected Puerto Rico Esports League case studies across brand activations, original content, live events, and esports-driven experiences.",
    ogDescription: "Concrete examples of PREL's work across media, events, and entertainment crossovers.",
    schemaName: "Selected Work | Puerto Rico Esports League",
    schemaDescription: "Case studies and examples of Puerto Rico Esports League's work.",
    eyebrow: "Selected Work",
    title: "Cases that show how PREL operates.",
    intro:
      "PREL works across live experiences, brand moments, original content, and gaming-centered storytelling. This page gathers examples that help explain how the platform works in practice and why it can matter to brands, collaborators, media, and strategic partners.",
    why: "Why it mattered",
    items: [
      {
        title: "PREL x WWE 2K",
        category: "Entertainment crossover",
        summary:
          "A moment that showed PREL's ability to connect gaming culture with recognizable IP, talent-driven storytelling, and brand relevance in front of the audience.",
        outcome:
          "It helped position PREL as more than a local tournament platform, proving it can also create culturally relevant moments with media visibility and expansion potential.",
        assetLabel: "View WWE sample",
        assetHref: "/media/PREL_WWE_Edit.mp4",
      },
      {
        title: "US Army Battleground",
        category: "Live activation",
        summary:
          "A high-visibility competitive event that combined live production, brand presence, audience participation, and content useful for recap and later distribution.",
        outcome:
          "It demonstrated PREL's ability to execute in-person experiences that work both inside the venue and as post-event content for broader visibility.",
        assetLabel: "Watch recap",
        assetHref: "https://www.youtube.com/watch?v=SfsotLUQde0",
      },
      {
        title: "PREL: The Show",
        category: "Television and original content",
        summary:
          "PREL's flagship program established a repeatable format between television and digital, creating a media property for gaming, talent, guests, and original storytelling.",
        outcome:
          "It strengthened audience development, partner integration, and long-term media positioning beyond live events.",
        assetLabel: "Explore The Show",
        assetHref: "/the-show",
      },
      {
        title: "School Wars",
        category: "Community and youth",
        summary:
          "A school-focused competitive format built around participation, visibility, and emerging talent, helping PREL operate at the intersection of gaming, youth, and structured programming.",
        outcome:
          "It expanded PREL's relevance beyond core competition by showing how the platform can also support community activation and future audience growth.",
        assetLabel: "View sample",
        assetHref: "https://www.youtube.com/watch?v=JO5b4eYqhEs",
      },
    ],
  },
};

export default function SelectedWorkPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const copy = copyByLang[lang];

  useEffect(() => {
    document.title = copy.metaTitle;

    const setMeta = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property='${name}']` : `meta[name='${name}']`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", copy.metaDescription);
    setMeta("og:title", copy.metaTitle, true);
    setMeta("og:description", copy.ogDescription, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", "https://www.puertoricoesportleague.com/selected-work", true);
    setMeta("og:image", "https://www.puertoricoesportleague.com/logo.png", true);

    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.puertoricoesportleague.com/selected-work");

    const schemaId = "prel-selected-work-schema";
    let schemaTag = document.getElementById(schemaId);
    if (!schemaTag) {
      schemaTag = document.createElement("script");
      schemaTag.type = "application/ld+json";
      schemaTag.id = schemaId;
      document.head.appendChild(schemaTag);
    }
    schemaTag.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: copy.schemaName,
      url: "https://www.puertoricoesportleague.com/selected-work",
      description: copy.schemaDescription,
    });
  }, [copy]);

  return (
    <div className="min-h-screen bg-[#06070A] text-white">
      <SiteHeader current="/selected-work" />

      <main className="max-w-[1400px] mx-auto px-6 xl:px-8 pt-12 pb-20">
        <p className="text-xs tracking-[0.3em] uppercase text-[#CE1126]">{copy.eyebrow}</p>
        <h1 className="mt-4 text-[clamp(2.6rem,8vw,6rem)] font-black leading-[0.9] uppercase">
          {copy.title}
        </h1>
        <p className="mt-6 max-w-4xl text-slate-300 text-lg leading-relaxed">
          {copy.intro}
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {copy.items.map((item) => (
            <article key={item.title} className="border border-white/10 bg-[#111722] p-6 md:p-7 flex flex-col">
              <p className="text-xs uppercase tracking-[0.22em] text-cyan-300">{item.category}</p>
              <h2 className="mt-3 text-2xl md:text-3xl font-black">{item.title}</h2>
              <p className="mt-4 text-slate-300 leading-relaxed">{item.summary}</p>

              <div className="mt-5 border-t border-white/10 pt-5">
                <p className="text-xs uppercase tracking-[0.2em] text-[#CE1126]">{copy.why}</p>
                <p className="mt-3 text-slate-300 leading-relaxed">{item.outcome}</p>
              </div>

              <div className="mt-6">
                <a href={item.assetHref} target={item.assetHref.startsWith("http") ? "_blank" : undefined} rel={item.assetHref.startsWith("http") ? "noopener noreferrer" : undefined} className="inline-flex px-4 py-2 border border-white/15 text-white hover:bg-white/5 transition">
                  {item.assetLabel}
                </a>
              </div>
            </article>
          ))}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
